/**
 * ThemeProvider - SOC palette light/dark mode plumbing.
 *
 * Phase 4 amendment 2026-04-27: ship both modes with a localStorage-persisted
 * toggle exposed via a `useTheme()` hook. The provider sets the
 * `data-theme="dark|light"` attribute on `document.documentElement`, which
 * matches the selectors bound in tokens.css.ts so every CSS variable
 * resolves to the active palette.
 *
 * Resolution order on mount:
 *   1. localStorage('soc-theme')  ('dark' | 'light')
 *   2. window.matchMedia('(prefers-color-scheme: dark)')
 *   3. fallback: 'light'
 *
 * The toggle setter persists to localStorage and re-applies the attribute
 * synchronously (no flash). The attribute change drives all consuming styles
 * via the data-theme selector binding in tokens.css.ts.
 *
 * Surface area:
 *   - <ThemeProvider> wraps the app once in main.tsx.
 *   - useTheme() returns { mode, setMode, toggleMode } for any component
 *     that wants to read or change the active mode (TopNav user menu).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** The two SOC modes. */
export type ThemeMode = 'dark' | 'light';

/** localStorage key. Namespaced so it cannot collide with other apps. */
const STORAGE_KEY = 'soc-theme';

/** Context value shape. */
interface ThemeContextValue {
  mode: ThemeMode;
  setMode: (next: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Resolve the initial mode synchronously so the first paint already has the
 * right palette applied (no flash of light when the user has dark persisted).
 *
 * Reads localStorage first, then prefers-color-scheme, then defaults to light.
 * Safe-guards against SSR / non-browser environments via typeof window check.
 */
function resolveInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {
    // localStorage may throw under privacy modes; fall through to OS query.
  }
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Apply the mode to the document root by setting `data-theme` and persisting
 * to localStorage. Callable independently of React lifecycle so the initial
 * paint pre-React can also use it (see the inline boot script in index.html).
 */
function applyMode(next: ThemeMode): void {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', next);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // localStorage may throw under privacy modes; ignore (in-memory only).
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Wrap the app once. Sets the initial mode on mount + reapplies on change.
 * Children consume via `useTheme()`.
 */
export function ThemeProvider({ children }: ThemeProviderProps): JSX.Element {
  const [mode, setModeState] = useState<ThemeMode>(() => resolveInitialMode());

  // Apply on mount (covers the case where the index.html boot script did not
  // run, e.g. SSR / test environment) and on every change.
  useEffect(() => {
    applyMode(mode);
  }, [mode]);

  // Public setter: update React state; the effect above persists + applies.
  const setMode = useCallback((next: ThemeMode): void => {
    setModeState(next);
  }, []);

  // Convenience toggle for the TopNav button.
  const toggleMode = useCallback((): void => {
    setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Memoize so consumers do not re-render when an unrelated parent re-renders.
  const value = useMemo<ThemeContextValue>(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Read + change the active SOC mode. Throws when called outside a
 * <ThemeProvider> so missing-provider bugs surface in dev rather than
 * silently producing a "stuck on dark" UI.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === null) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}
